import { Box, TextField } from "@mui/material";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";

// Miles con coma y decimales con punto (1,234.56). El valor que se guarda
// no lleva comas, para que el API siga recibiendo un decimal.
export const formatoMonto = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const number = typeof value === "number" ? value : Number(String(value).replace(/,/g, ""));
    if (!Number.isFinite(number)) return "";
    return number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const textoPlano = (value) => {
    if (value === null || value === undefined || value === "") return "";
    return String(value).replace(/,/g, "");
};

// Mientras se escribe, la coma sola es el decimal. Si tambien hay punto,
// la coma es el separador de miles.
const entradaEditable = (text) => {
    const clean = String(text).replace(/\s/g, "");
    if (clean.includes(".")) return clean.replace(/,/g, "");
    const commas = (clean.match(/,/g) || []).length;
    if (commas <= 1) return clean.replace(",", ".");
    return clean.replace(/,/g, "");
};

const MontoTextField = ({
    value,
    name,
    onChange,
    inputProps,
    InputProps,
    InputLabelProps,
    disabled = false,
    readOnly = false,
    step = 1,
    min = 0,
    ...props
}) => {
    const [focused, setFocused] = useState(false);
    const [draft, setDraft] = useState("");
    const [dirty, setDirty] = useState(false);
    const rootRef = useRef(null);

    const soloLectura = disabled || readOnly || InputProps?.readOnly;

    useEffect(() => {
        if (focused && !dirty) setDraft(textoPlano(value));
    }, [value, focused, dirty]);

    const emitir = (raw) => {
        onChange?.({ target: { name, value: raw } });
    };

    const incrementar = (delta) => {
        if (soloLectura) return;
        const actualStr = focused ? draft : textoPlano(value);
        let numActual = parseFloat(actualStr);
        if (isNaN(numActual)) numActual = 0;

        let nuevoNum = Math.round((numActual + (delta * step)) * 100) / 100;
        const limiteMin = inputProps?.min !== undefined ? Number(inputProps.min) : min;
        if (!isNaN(limiteMin) && nuevoNum < limiteMin) {
            nuevoNum = limiteMin;
        }

        const nuevoStr = nuevoNum.toFixed(2);
        setDirty(true);
        setDraft(nuevoStr);
        emitir(nuevoStr);
    };

    const incrementarRef = useRef();
    incrementarRef.current = incrementar;

    useEffect(() => {
        const el = rootRef.current;
        if (!el || soloLectura) return;

        const onWheel = (e) => {
            e.preventDefault();
            if (e.deltaY < 0) {
                incrementarRef.current?.(1);
            } else if (e.deltaY > 0) {
                incrementarRef.current?.(-1);
            }
        };

        el.addEventListener("wheel", onWheel, { passive: false });
        return () => el.removeEventListener("wheel", onWheel);
    }, [soloLectura]);

    const handleKeyDown = (event) => {
        if (event.key === "ArrowUp") {
            event.preventDefault();
            incrementar(1);
        } else if (event.key === "ArrowDown") {
            event.preventDefault();
            incrementar(-1);
        }
        props.onKeyDown?.(event);
    };

    const stepper = !soloLectura ? (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                ml: 0.25,
                userSelect: "none",
            }}
        >
            <Box
                component="button"
                type="button"
                tabIndex={-1}
                title="Incrementar 1"
                aria-label="Incrementar 1"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    incrementar(1);
                }}
                sx={{
                    p: 0,
                    m: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 11,
                    width: 16,
                    color: "action.active",
                    lineHeight: 1,
                    "&:hover": { color: "primary.main" },
                }}
            >
                <ArrowDropUp sx={{ fontSize: 18 }} />
            </Box>
            <Box
                component="button"
                type="button"
                tabIndex={-1}
                title="Disminuir 1"
                aria-label="Disminuir 1"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    incrementar(-1);
                }}
                sx={{
                    p: 0,
                    m: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 11,
                    width: 16,
                    color: "action.active",
                    lineHeight: 1,
                    "&:hover": { color: "primary.main" },
                }}
            >
                <ArrowDropDown sx={{ fontSize: 18 }} />
            </Box>
        </Box>
    ) : null;

    return (
        <TextField
            {...props}
            ref={rootRef}
            disabled={disabled}
            name={name}
            value={focused ? draft : formatoMonto(value)}
            onFocus={() => {
                setFocused(true);
                setDirty(false);
                setDraft(textoPlano(value));
            }}
            onChange={(event) => {
                const next = entradaEditable(event.target.value);
                if (next !== "" && !/^\d*\.?\d*$/.test(next)) return;
                setDirty(true);
                setDraft(next);
                emitir(next);
            }}
            onKeyDown={handleKeyDown}
            onBlur={() => {
                setFocused(false);
                if (!dirty) return;
                if (draft === "" || draft === ".") {
                    emitir("");
                    return;
                }
                const number = Number(draft);
                if (!Number.isFinite(number)) {
                    emitir("");
                    return;
                }
                emitir(number.toFixed(2));
            }}
            inputProps={{
                ...inputProps,
                inputMode: "decimal",
                style: { textAlign: "right", ...(inputProps?.style || {}) },
            }}
            InputProps={{
                ...InputProps,
                readOnly: readOnly || InputProps?.readOnly,
                endAdornment: (
                    <>
                        {InputProps?.endAdornment}
                        {stepper}
                    </>
                ),
            }}
            InputLabelProps={{ shrink: true, ...InputLabelProps }}
        />
    );
};

export default MontoTextField;
